import {FC, useCallback, useEffect} from "react";

import {AugmentedEvent, SimulationEventType} from "@repo/shared";

import {useGame} from "~/contexts/game-manager/GameManagerProvider";

import {useAudio} from "./AudioContext";

/**
 * A controller component that listens to game events from the GameManager
 * and plays corresponding sound effects.
 */
const GameSoundEffects: FC = () => {
  const {
    playCollision,
    playAttractorImpulse,
    playShardCollected,
    playNewPersonalBest, // This will be used for any level completion
    playPlayerDied,
  } = useAudio();

  const {gameManager} = useGame();

  // A single, unified handler for all relevant simulation events.
  const handleSimulationEvent = useCallback(
    (event: AugmentedEvent) => {
      // We only want to play sounds for the primary game the user is focused on.
      if (!event.isPrimary) {
        return;
      }

      switch (event.type) {
        case SimulationEventType.PlayerHitShard:
          playShardCollected();
          break;

        case SimulationEventType.PlayerHitHazard:
        case SimulationEventType.PlayerOutOfBounds:
          playPlayerDied();
          break;

        case SimulationEventType.PlayerInAttractorZone:
          playAttractorImpulse();
          break;

        case SimulationEventType.PlayerHitLine:
          if (event.contact && event.material) {
            if (event.contact.velocityAlongNormal < 0) {
              const impulseMag =
                -event.contact.velocityAlongNormal *
                (1 + event.material.restitution);
              const normalizedVolume = Math.min(impulseMag / 10, 1);
              playCollision(normalizedVolume);
            }
          }
          break;
      }
    },
    [playShardCollected, playPlayerDied, playAttractorImpulse, playCollision],
  );

  // A handler for when a round finishes.
  const handleRoundFinished = useCallback(
    (result: {completed: boolean}, isPrimary: boolean) => {
      if (!isPrimary) return;

      // If the round was completed successfully, play the sound.
      if (result.completed) {
        playNewPersonalBest(); // Play this sound for every successful completion.
      }
    },
    [playNewPersonalBest],
  );

  // Subscribe to GameManager events
  useEffect(() => {
    if (!gameManager) return;

    gameManager.on("simulation-event", handleSimulationEvent);
    gameManager.on("round-finished", handleRoundFinished);

    // Cleanup function to unsubscribe from all events
    return () => {
      gameManager.off("simulation-event", handleSimulationEvent);
      gameManager.off("round-finished", handleRoundFinished);
    };
  }, [gameManager, handleSimulationEvent, handleRoundFinished]);

  // No other global event listeners are needed.

  return null; // This component doesn't render anything
};

export default GameSoundEffects;
